/**
 * Render text with its spans highlighted.
 *
 * Deliberately thin: all the correctness lives in `lib/highlight`, which is pure and
 * unit-tested against the emoji, combining-mark and homoglyph cases. This component
 * only decides what a marked run looks like.
 */

import type { Span } from "../lib/api";
import { excerpt, segment, spanMismatch } from "../lib/highlight";

interface Props {
  text: string;
  spans: readonly Span[];
  /** Show a window around the first span instead of the whole block. */
  around?: number;
}

export function Marked({ text, spans, around }: Props) {
  const mismatched = spanMismatch(text, spans);
  if (mismatched.length > 0) {
    // Refuse rather than mislead. A highlight that disagrees with its own quote looks
    // exactly like a finding, which makes it more dangerous than a missing one.
    return (
      <span data-testid="span-mismatch" style={{ color: "var(--danger)" }}>
        {text}
      </span>
    );
  }

  const first = spans[0];
  const parts =
    around !== undefined && first ? excerpt(text, first, around) : segment(text, spans);

  return (
    <>
      {parts.map((part, index) =>
        part.marked ? (
          <mark className="span" data-testid="span" key={index}>
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}
